import React, { useEffect, useRef } from "react";
import cx from "classnames";

import routeWithUserSession from "../../components/application-area/routeWithUserSession";
import Link from "@/components/Link";
import Tooltip from "@/components/Tooltip";
import PageHeader from "@/components/page-header";
import Paginator from "@/components/items-list/components/Paginator";
import DynamicComponent from "@/components/DynamicComponent";


import { wrap as itemsList, ControllerType } from "@/components/items-list/ItemsList";
import useItemsListExtraActions from "@/components/items-list/hooks/useItemsListExtraActions";
import { ResourceItemsSource } from "@/components/items-list/classes/ItemsSource";
import { UrlStateStorage } from "@/components/items-list/classes/StateStorage";

import * as Sidebar from "@/components/items-list/components/Sidebar";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";

import Layout from "@/components/layouts/ContentWithSidebar";

import { AlertEvent } from "@/services/alert-events";
import { currentUser } from "@/services/auth";
import location from "@/services/location";
import routes from "@/services/routes";
import TimeAgo from "@/components/TimeAgo";
import AlertEventsListEmptyState from "./AlertEventsListEmptyState";
import JsonViewInteractive from "@/components/json-view-interactive/JsonViewInteractive";
import QuestionCircleOutlined from "@ant-design/icons/QuestionCircleOutlined";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";

import "./alert-events.css";
import { AlertEventTagsControl } from "@/components/tags-control/TagsControl";
import {
  DiscordMessages,
  DiscordMessage,
  DiscordEmbed,
  DiscordEmbedFields,
  DiscordEmbedField
} from '@skyra/discord-components-react';
import { Tweet } from 'react-fake-tweet'
import 'react-fake-tweet/dist/index.css'



const sidebarMenu = [
  {
    key: "all",
    href: "alert_events",
    title: "All Alert Events",
    icon: () => <Sidebar.MenuIcon icon="fa fa-cubes" />,
  },
  {
    key: "my",
    href: "alert_events/my",
    title: "My Alert Events",
    icon: () => <Sidebar.ProfileImage user={currentUser} />,
  },
  {
    key: "favorites",
    href: "alert_events/favorites",
    title: "Favorites",
    icon: () => <Sidebar.MenuIcon icon="fa fa-star" />,
  },
  {
    key: "archive",
    href: "alert_events/archive",
    title: "Archived",
    icon: () => <Sidebar.MenuIcon icon="fa fa-archive" />,
  },
];

const listColumns = [
  Columns.favorites({ className: "p-r-0" }),
  Columns.custom.sortable(
    (text, event) => {
      let parsedProperties;
      let contentText;

      try {
        parsedProperties = JSON.parse(event.additional_properties);
        contentText = typeof event.content === 'string' ? event.content.toLowerCase() : JSON.stringify(event.content).toLowerCase();
      } catch (e) {
        parsedProperties = event.additional_properties;
        contentText = event.content;
      }
  
      const statusProperties = parsedProperties?.status?.toLowerCase();
      const statusContent = contentText.toLowerCase().includes('error') ? 'error' : 'ok';
  
      const status =
        statusProperties === "ok" && statusContent === "ok"
          ? "ok"
          : statusProperties === "error" || statusContent === "error"
          ? "error"
          : null;
  
      return (
        <div align="center">
          {status === 'ok' && (
            <Link href={"alert_events/" + event.id}>
              <Tooltip title="OK">
                <CheckCircleOutlined style={{ color: 'green', fontSize: '16px' }} />
              </Tooltip>
            </Link>
          )}
          {status === 'error' && (
            <Link href={"alert_events/" + event.id}>
              <Tooltip title="Error">
                <ExclamationCircleOutlined style={{ color: 'red', fontSize: '16px' }} />
              </Tooltip>
            </Link>
          )}
          {status === null && (
            <Link href={"alert_events/" + event.id}>
              <Tooltip title="Unknown">
                <QuestionCircleOutlined style={{ color: 'orange', fontSize: '16px' }} />
              </Tooltip>
            </Link>
          )}
        </div>
      );
    },
    {
      title: "Status",
      field: "status", // Use 'status' to reference in custom sorting
      width: "1%",
      sorter: true,
    }
  ),
  Columns.custom.sortable(
    (text, event) => (
      <div>
        <Tooltip title={<div align="center">{new Date(event.created_at).toLocaleString()}<br/><TimeAgo date={event.created_at} /></div>}>
          {new Date(event.created_at).toLocaleDateString()}
        </Tooltip>
      </div>
      ),
      {
        title: "Date", 
        field: "created_at", 
        width: "10%" 
      }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
      <React.Fragment>
          <Link className="table-main-title"  href={"alert_events/" + alert_event.id}>{alert_event.id}</Link>
          <AlertEventTagsControl className="d-block" tags={alert_event.tags} isArchived={alert_event.is_archived} />
      </React.Fragment>
      </div>
    ),
    {
      title: "ID",
      field: "id",
      width: "1%",
    }
  ),
  Columns.custom.sortable((text, event) => event.alert_type, {
    title: "Alert Type",
    field: "alert_type",
    width: "1%",
  }),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.alert ? (
          <Link href={"alerts/" + alert_event.alert.id+"/overview"}>{alert_event.alert.name}</Link>
        ) : (
          "No alert"
        )}
      </div>
    ),
    {
      title: "Alert Name",
      field: "alert.name",
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.query ? (
          <Link href={"queries/" + alert_event.query.id}>{alert_event.query.name}</Link>
        ) : (
          "No query"
        )}
      </div>
    ),
    {
      title: "Query Name",
      field: "query.name",
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.user ? (
          <Link href={"users/" + alert_event.user.id }>{alert_event.user.name}</Link>
        ) : (
          "No user"
        )}
      </div>
    ),
    {
      title: "User Name",
      field: "user.name",
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.destination ? (
          <Link href={"destinations/" + alert_event.destination.id}>{alert_event.destination.name}</Link>
        ) : (
          "No destination"
        )}
      </div>
    ),
    {
      title: "Destination Name",
      field: "destination.name",
      width: "1%",
    }
  ),
  Columns.centeredWrappableExpandable(
    (text, event) => {
      let parsedContent;
      try {
        parsedContent = JSON.parse(event.content);
      } catch (e) {
        parsedContent = event.content;
      }
      
      function convertMarkdownLinks(text) {
        const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        return text.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      }

      function decimalColortoHextString(decimalColor) {
        return '#' + decimalColor.toString(16).padStart(6, '0');
      }

      return (
        <div>
          {event.alert_type === "discord_webhook" ? (
                    <DiscordMessages>
                      <DiscordMessage author={event?.destination?.options?.username} avatar={event?.destination?.options?.avatar_url} timestamp={new Date(event?.created_at).toLocaleDateString() }>
                      <DiscordEmbed
                        slot="embeds"
                        color={parsedContent["embeds"][0]?.color ? decimalColortoHextString(parsedContent["embeds"][0]?.color) : "#0099ff"}
                        embedTitle={parsedContent["embeds"][0]?.title}
                      >
                        <div dangerouslySetInnerHTML={{ __html: convertMarkdownLinks(parsedContent["embeds"][0]?.description || '') }} />
                        {parsedContent["embeds"][0]?.fields && (
                          <DiscordEmbedFields slot="fields">
                            {parsedContent["embeds"][0].fields.map((field, index) => (
                              <DiscordEmbedField key={index} fieldTitle={field.name} inline={field.inline}>
                                <div dangerouslySetInnerHTML={{ __html: convertMarkdownLinks(field.value) }} />
                              </DiscordEmbedField>
                            ))}
                          </DiscordEmbedFields>
                        )}
                      </DiscordEmbed>
                    </DiscordMessage>
                    </DiscordMessages>
                  ) : event.alert_type === "twitter" ?  (
                    <Tweet
                        config={{
                          user: {
                            avatar: event?.destination?.options?.avatar_url,
                            nickname: event?.destination?.options?.account_handle,
                            name: event?.destination?.name,
                          },
                          text: (
                            <span style={{ fontSize: '14px' }}>
                              {event.content}
                            </span>
                          ),
                          date: event?.created_at,
                        }}
                      />
                  ):""}
        </div>
      );
    },
    { title: "Content", field: "content", width: "20%" }
  ),
  Columns.centered(
    (text, event) => {
      let parsedProperties;
      try {
        parsedProperties = JSON.parse(event.additional_properties);
      } catch (e) {
        parsedProperties = event.additional_properties;
      }

      return (
        <div className="json-cell">
          {typeof parsedProperties === "object" ? (
            <JsonViewInteractive value={parsedProperties} />
          ) : (
            <pre>{String(parsedProperties)}</pre>
          )}
        </div>
      );
    },
    { title: "Additional Properties", 
      field: "additional_properties", 
      width: "20%" }
  ),
];

function AlertEventsListExtraActions(props) {
  return <DynamicComponent name="AlertEventsList.Actions" {...props} />;
}

function AlertEventsList({ controller }) {
  const controllerRef = useRef();
  controllerRef.current = controller;

  useEffect(() => {
    const unlistenLocationChanges = location.listen((unused, action) => {
      const searchTerm = location.search.q || "";
      if (action === "PUSH" && searchTerm !== controllerRef.current.searchTerm) {
        controllerRef.current.updateSearch(searchTerm);
      }
    });

    return () => {
      unlistenLocationChanges();
    };
  }, []);

  const {
    areExtraActionsAvailable,
    listColumns: tableColumns,
    Component: ExtraActionsComponent,
    selectedItems,
  } = useItemsListExtraActions(controller, listColumns, AlertEventsListExtraActions);

  return (
    <div className="page-alerts-list">
      <div className="container">
        <PageHeader
          title={controller.params.pageTitle}
          actions={
            currentUser.hasPermission("list_alerts") ? (
              <Link.Button block type="primary" href="alerts/new">
                <i className="fa fa-plus m-r-5" aria-hidden="true" />
                New Alert
              </Link.Button>
            ) : null
          }
        />
        <Layout>
          <Layout.Sidebar className="m-b-0">
            <Sidebar.SearchInput
              placeholder="Search Alert Events..."
              label="Search alerts"
              value={controller.searchTerm}
              onChange={controller.updateSearch}
            />
            <Sidebar.Menu items={sidebarMenu} selected={controller.params.currentPage} />
            <Sidebar.Tags url="api/alert_events/tags" onChange={controller.updateSelectedTags} showUnselectAll />
          </Layout.Sidebar>
          <Layout.Content>
            {controller.isLoaded && controller.isEmpty ? (
              <AlertEventsListEmptyState
                page={controller.params.currentPage}
                searchTerm={controller.searchTerm}
                selectedTags={controller.selectedTags}
              />
            ) : (
              <React.Fragment>
                <div className={cx({ "m-b-10": areExtraActionsAvailable })}>
                  <ExtraActionsComponent selectedItems={selectedItems} />
                </div>
                <div className="bg-white tiled table-responsive">
                  <ItemsTable
                    items={controller.pageItems}
                    loading={!controller.isLoaded}
                    columns={tableColumns}
                    orderByField={controller.orderByField}
                    orderByReverse={controller.orderByReverse}
                    toggleSorting={controller.toggleSorting}
                  />
                  <Paginator
                    showPageSizeSelect
                    totalCount={controller.totalItemsCount}
                    pageSize={controller.itemsPerPage}
                    onPageSizeChange={itemsPerPage => controller.updatePagination({ itemsPerPage })}
                    page={controller.page}
                    onChange={page => controller.updatePagination({ page })}
                  />
                </div>
              </React.Fragment>
            )}
          </Layout.Content>
        </Layout>
      </div>
    </div>
  );
}

AlertEventsList.propTypes = {
  controller: ControllerType.isRequired,
};

const AlertEventsListPage = itemsList(
  AlertEventsList,
  () =>
    new ResourceItemsSource({
      getResource({ params: { currentPage } }) {
        return {
          all: AlertEvent.query.bind(AlertEvent),
          my: AlertEvent.myAlertEvents.bind(AlertEvent),
          favorites: AlertEvent.favorites.bind(AlertEvent),
          archive: AlertEvent.archive.bind(AlertEvent),
        }[currentPage];
      },
      getItemProcessor() {
        return item => new AlertEvent(item);
      },
    }),
  () => new UrlStateStorage({ orderByField: "created_at", orderByReverse: true, itemsPerPage: 100 })
);

routes.register(
  "AlertEvents.List",
  routeWithUserSession({
    path: "/alert_events",
    title: "Alerts History",
    render: pageProps => <AlertEventsListPage {...pageProps} currentPage="all" />,
  })
);
routes.register(
  "AlertEvents.Favorites",
  routeWithUserSession({
    path: "/alert_events/favorites",
    title: "Favorite Events",
    render: pageProps => <AlertEventsListPage {...pageProps} currentPage="favorites" />,
  })
);
routes.register(
  "AlertEvents.Archived",
  routeWithUserSession({
    path: "/alert_events/archive",
    title: "Archived Events",
    render: pageProps => <AlertEventsListPage {...pageProps} currentPage="archive" />,
  })
);
routes.register(
  "AlertEvents.My",
  routeWithUserSession({
    path: "/alert_events/my",
    title: "My Events",
    render: pageProps => <AlertEventsListPage {...pageProps} currentPage="my" />,
  })
);
